/**
 * Shell CSS — and ONLY shell CSS.
 *
 * `baseStylesheet()` in @clawharbor/render-core already paints every shared
 * component (.ch-card, .ch-chip, .ch-btn, .ch-table, .ch-empty, .ch-disclaimer,
 * …) from the tenant's --ch-* tokens. Duplicating any of that here would let the
 * page and the MCP Apps widget drift apart, which is the one thing the shared
 * package exists to prevent.
 *
 * So this file adds only what has no widget equivalent: the page frame, the
 * capability tabs, the human input panel, the agent debug strip, the staged-
 * selection panel and the footer. Those use a `.chw-` prefix precisely so they
 * can never collide with render-core's `.ch-` layer.
 *
 * Every value is a `var(--ch-…, literal)`. The Palette schema only guarantees
 * brand.primary, bg.canvas and fg.default, so a tenant whose extraction found no
 * bg.raised must still render rather than disappear.
 *
 * Mobile-first: the base rules are the phone layout; there are two breakpoints.
 */
export const shellStylesheet = `
*, *::before, *::after { box-sizing: border-box; }
html { -webkit-text-size-adjust: 100%; }

body {
  margin: 0;
  background: var(--ch-bg-canvas, #ffffff);
  color: var(--ch-fg-default, #111111);
  font-family: var(--ch-font-sans, system-ui, sans-serif);
  font-size: var(--ch-font-size-base, 16px);
  line-height: 1.45;
  min-height: 100vh;
}

.chw-shell { max-width: 68rem; margin: 0 auto; padding: 0 var(--ch-space-3, 12px) var(--ch-space-6, 32px); }

/* The disclaimer bar is render-core's .ch-disclaimer; this only pins it above the fold. */
.chw-disclaimer-slot {
  position: sticky; top: 0; z-index: 10;
  background: var(--ch-bg-raised, var(--ch-bg-surface, #f4f4f4));
  border-bottom: var(--ch-border-width, 1px) solid var(--ch-border-default, #e6e6e6);
}
.chw-disclaimer-slot .ch-disclaimer { max-width: 68rem; margin: 0 auto; border: 0; }

/* ---- masthead (page identity; distinct from render-core's per-view .ch-header) ---- */

.chw-masthead {
  display: flex; flex-direction: column; gap: var(--ch-space-2, 8px);
  padding: var(--ch-space-4, 16px) 0 var(--ch-space-3, 12px);
}
.chw-wordmark {
  font-family: var(--ch-font-display, var(--ch-font-sans, system-ui, sans-serif));
  font-size: var(--ch-size-xl, 30px);
  font-weight: var(--ch-font-weight-heading, 700);
  text-transform: var(--ch-case-heading, none);
  color: var(--ch-brand-primary, #111111);
  line-height: 1.1;
  margin: 0;
}
.chw-logo { display: block; max-width: 100%; height: auto; }
.chw-tagline { color: var(--ch-fg-muted, #5c5c5c); font-size: var(--ch-size-sm, 14px); margin: 0; }
.chw-masthead-meta { display: flex; flex-wrap: wrap; gap: var(--ch-space-2, 8px); align-items: center; }

/* ---- agent debug strip: makes an invisible tool call provable ---- */

.chw-debug {
  display: flex; flex-wrap: wrap; align-items: center; gap: var(--ch-space-2, 8px);
  font-family: var(--ch-font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 12px; line-height: 1.6;
  padding: var(--ch-space-2, 8px) var(--ch-space-3, 12px);
  border-radius: var(--ch-radius-card, 8px);
  background: var(--ch-bg-surface, rgba(127,127,127,.10));
  border: var(--ch-border-width, 1px) solid var(--ch-border-default, #e6e6e6);
  margin-bottom: var(--ch-space-4, 16px);
  overflow-x: auto;
}
.chw-led { width: 9px; height: 9px; border-radius: 999px; flex: none; background: var(--ch-fg-muted, #5c5c5c); }
.chw-led.on { background: var(--ch-state-success, #1e7d32); }
.chw-sep { opacity: .45; }
.chw-bad { color: var(--ch-state-danger, #b3261e); }

/* ---- capability tabs ---- */

.chw-tabs { display: flex; gap: var(--ch-space-2, 8px); overflow-x: auto; padding-bottom: 2px; margin-bottom: var(--ch-space-3, 12px); }
.chw-tab {
  flex: none; appearance: none; cursor: pointer; font: inherit;
  font-size: var(--ch-size-sm, 14px);
  text-transform: var(--ch-case-button, none);
  padding: 8px 14px;
  border-radius: var(--ch-radius-chip, 999px);
  border: var(--ch-border-width, 1px) solid var(--ch-border-default, #e6e6e6);
  background: transparent; color: inherit;
  transition: background var(--ch-motion-duration, 180ms) var(--ch-motion-easing, ease);
}
.chw-tab[aria-selected="true"] {
  background: var(--ch-brand-primary, #1a56db);
  color: var(--ch-brand-onPrimary, #ffffff);
  border-color: var(--ch-brand-primary, #1a56db);
  font-weight: 700;
}

/* ---- human input panel: the same schema the agent gets, as controls ---- */

.chw-panel {
  background: var(--ch-bg-surface, #ffffff);
  border: var(--ch-border-width, 1px) solid var(--ch-border-default, #e6e6e6);
  border-radius: var(--ch-radius-card, 8px);
  padding: var(--ch-space-3, 12px);
  margin-bottom: var(--ch-space-4, 16px);
}
.chw-panel-desc { color: var(--ch-fg-muted, #5c5c5c); font-size: var(--ch-size-sm, 14px); margin: 0 0 var(--ch-space-3, 12px); }
.chw-fields { display: grid; grid-template-columns: 1fr; gap: var(--ch-space-3, 12px); }
.chw-field { display: flex; flex-direction: column; gap: 4px; min-width: 0; border: 0; padding: 0; margin: 0; }
.chw-field > label, .chw-field > legend { font-size: var(--ch-size-xs, 12px); font-weight: 700; padding: 0; }
.chw-hint { font-size: 12px; color: var(--ch-fg-muted, #5c5c5c); }
.chw-field input[type="text"], .chw-field input[type="date"], .chw-field input[type="time"],
.chw-field input[type="number"], .chw-field select {
  font: inherit; width: 100%; padding: 9px 10px; color: inherit;
  background: var(--ch-bg-canvas, #ffffff);
  border: var(--ch-border-width, 1px) solid var(--ch-border-default, #e6e6e6);
  border-radius: var(--ch-radius-control, 8px);
}
.chw-field :focus-visible, .chw-tab:focus-visible {
  outline: 2px solid var(--ch-brand-primary, #1a56db); outline-offset: 2px;
}
.chw-checks { display: flex; flex-wrap: wrap; gap: var(--ch-space-2, 8px); }
.chw-check { display: inline-flex; align-items: center; gap: 6px; font-size: var(--ch-size-sm, 14px); font-weight: 400; }
.chw-actions { display: flex; flex-wrap: wrap; gap: var(--ch-space-2, 8px); align-items: center; margin-top: var(--ch-space-3, 12px); }
.chw-results-head { display: flex; flex-wrap: wrap; align-items: center; gap: var(--ch-space-2, 8px); margin-bottom: var(--ch-space-2, 8px); }

/* ---- staged: proposed by an agent, committed only by a human ---- */

.chw-staged {
  border: 2px solid var(--ch-brand-primary, #1a56db);
  border-radius: var(--ch-radius-card, 8px);
  padding: var(--ch-space-3, 12px);
  margin-bottom: var(--ch-space-4, 16px);
  background: var(--ch-bg-raised, #f4f4f4);
}
.chw-staged-label { font-size: 12px; text-transform: uppercase; letter-spacing: .06em; color: var(--ch-fg-muted, #5c5c5c); }

.chw-error {
  border-left: 4px solid var(--ch-state-danger, #b3261e);
  background: var(--ch-bg-surface, #ffffff);
  padding: var(--ch-space-3, 12px);
  border-radius: var(--ch-radius-card, 8px);
  margin-bottom: var(--ch-space-3, 12px);
}

.chw-footer {
  margin-top: var(--ch-space-6, 32px);
  padding: var(--ch-space-4, 16px) 0;
  border-top: var(--ch-border-width, 1px) solid var(--ch-border-default, #e6e6e6);
  font-size: 12px; color: var(--ch-fg-muted, #5c5c5c);
  display: grid; gap: 6px;
}
.chw-footer a { color: inherit; text-decoration: underline; }

@media (min-width: 40rem) {
  .chw-shell { padding: 0 var(--ch-space-4, 16px) var(--ch-space-7, 48px); }
  .chw-masthead { flex-direction: row; align-items: flex-end; justify-content: space-between; padding-top: var(--ch-space-5, 24px); }
  .chw-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (min-width: 62rem) {
  .chw-fields { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (prefers-reduced-motion: reduce) {
  .chw-tab { transition: none; }
}
`;
